import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import CustomCalendar from '../components/CustomCalendar';
import { getTodayISO } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeachingJulieGrevenPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    payment_method: 'in_person'
  });
  const [loading, setLoading] = useState(false);

  const INSTRUCTOR_ID = 'julie-christiansen';
  const LESSON_PRICE = 65;

  useEffect(() => {
    fetchAllAvailability();
  }, []);

  // Quick booking function that takes timeSlot directly (avoids state race condition)
  const handleQuickBooking = async (timeSlot) => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    setLoading(true);
    try {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const bookingData = {
        instructor_id: INSTRUCTOR_ID,
        instructor_name: 'Julie Christiansen',
        date: dateStr,
        start_time: timeSlot,
        duration_minutes: 40,
        booking_type: 'greven',
        language: i18n.language,
        price: LESSON_PRICE,
        requires_payment: false,
        payment_package_id: null,
        client_name: user.name,
        client_email: user.email,
        client_phone: user.phone || '',
        payment_method: 'in_person'
      };

      await axios.post(`${API}/bookings`, bookingData);
      
      toast.success(t('Booking confirmed!') + ' ' + t('Pay on-site'));
      setTimeout(() => navigate('/booking-success'), 1500);
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDate) {
      fetchAvailability();
      // Auto-scroll to time slots section
      setTimeout(() => {
        const timeSlotsSection = document.getElementById('time-slots-section');
        if (timeSlotsSection) {
          timeSlotsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [selectedDate]);

  const fetchAllAvailability = async () => {
    try {
      const today = getTodayISO();
      const response = await axios.get(`${API}/availability/${INSTRUCTOR_ID}?start_date=${today}`);
      const dates = response.data.map(a => new Date(a.date));
      setAvailableDates(dates);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const fetchAvailability = async () => {
    try {
      // Format date as local date string to avoid timezone issues
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const response = await axios.get(`${API}/availability/${INSTRUCTOR_ID}?start_date=${dateStr}`);
      
      const dayAvailability = response.data.find(a => a.date === dateStr);
      if (dayAvailability) {
        setAvailableSlots(dayAvailability.time_slots || []);
      } else {
        setAvailableSlots([]);
        toast.info('No availability for this date');
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
      toast.error('Failed to load availability');
    }
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Please select a date and time');
      return;
    }

    if (!formData.client_name || !formData.client_email) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      // Format date as local date string to avoid timezone issues
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const bookingData = {
        instructor_id: INSTRUCTOR_ID,
        instructor_name: 'Julie Christiansen',
        date: dateStr,
        start_time: selectedTime,
        duration_minutes: 40,
        booking_type: 'greven',
        language: i18n.language,
        price: LESSON_PRICE,
        requires_payment: false,
        payment_package_id: null,
        ...formData
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      toast.success(t('Booking confirmed!') + ' ' + t('Pay on-site'));
      setTimeout(() => navigate('/booking-success'), 2000);
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="booking-page">
        <button
          onClick={() => navigate(-1)}
          className="back-arrow-btn"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="booking-header">
          <h1 className="booking-title" data-testid="page-title">{t('On-Site Training at JC-P')}</h1>
          <p style={{ color: '#8b4857', fontStyle: 'italic', marginBottom: '0.5rem' }}>{t('New address note')}</p>
          <p style={{ color: '#6b6b6b' }}>{t('Train live in our beautiful indoor arena - Wednesdays')}</p>
        </div>

        <div className="booking-two-column">
          {/* Left Column - Calendar */}
          <div className="booking-left">
            <CustomCalendar
              availableDates={availableDates}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
            />
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#8b4857', textAlign: 'center', fontWeight: 500 }}>
              {t('Colored dates are available for booking')}
            </p>
          </div>

          {/* Right Column - Available Times FIRST, then Information */}
          <div className="booking-right">
            {/* Available Times Section - Always visible */}
            <div className="form-group" id="time-slots-section" style={{ marginBottom: '2rem' }}>
              <label className="form-label" style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
                {t('Available Times')}
              </label>
              
              {!selectedDate ? (
                <p style={{ color: '#999', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  {t('Select date in calendar')}
                </p>
              ) : availableSlots.length === 0 ? (
                <p style={{ color: '#999' }}>{t('No available time slots')}</p>
              ) : (
                <>
                  <div className="time-slots-grid">
              {availableSlots.length === 0 ? (
                <p style={{ color: '#999' }}>No available time slots for this date</p>
              ) : (
                <div className="time-slots-grid">
                  {availableSlots.map((slot, idx) => (
                    <div
                      key={idx}
                      className={`time-slot ${
                        slot.booked ? 'booked' : selectedTime === slot.start_time ? 'selected' : ''
                      }`}
                      onClick={() => {
                        if (!slot.booked) {
                          if (isAuthenticated) {
                            // Check if user is approved
                            if (user.approved === false) {
                              toast.error(t('Your account is awaiting approval'));
                              return;
                            }
                            // Quick booking for logged in users - pass time directly to avoid state issues
                            if (window.confirm(`${t('Confirm booking')}\n\n${t('Date')}: ${selectedDate.toLocaleDateString()}\n${t('Time')}: ${slot.start_time}\n${t('Price')}: €${LESSON_PRICE}\n\n${t('Confirm')}?`)) {
                              handleQuickBooking(slot.start_time);
                            }
                          } else {
                            // Not logged in - ask to log in
                            toast.info('Du skal være logget ind for at booke');
                            setTimeout(() => {
                              navigate('/login');
                            }, 1500);
                          }
                        }
                      }}
                      data-testid={`time-slot-${slot.start_time}`}
                    >
                      {slot.start_time}
                    </div>
                  ))}
                </div>
              )}
                </div>
              </>
              )}
            </div>

            {/* About Section - Below Available Times */}
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', marginBottom: '1rem', fontFamily: "'Cormorant Garamond', serif" }}>
                {t('About On-Site Training')}
              </h2>
              <div style={{ color: '#6b6b6b', lineHeight: '1.7' }}>
                <p>{t('Train in person with Julie')}</p>
                <br />
                <p>
                  <strong>{t('About Julie')}</strong><br />
                  • {t('3x World Champion')}<br />
                  • {t('3x Nordic Champion')}<br />
                  • {t('26x Danish Champion')}<br />
                  • {t('Exceptional didactic skills')}
                </p>
                <br />
                <p>{t("Julie's philosophy")}</p>
                <br />
                <p><strong>{t('Price per lesson', { price: LESSON_PRICE })}</strong> {t('Pay on-site')}</p>
                <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}><em>{t('Available daytime and evening')}</em></p>
                <p style={{ fontSize: '0.875rem', marginTop: '0.75rem', color: '#7c6f5e' }}><em>{t('Greven session info')}</em></p>
              </div>
            </div>

            {selectedTime && !isAuthenticated && (
              <>
                <div className="section-title" style={{ marginTop: '2rem' }}>{t('Your Information')}</div>
                
                <div className="form-group">
                  <label className="form-label">{t('Full Name')}</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    data-testid="client-name-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('Email')}</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    data-testid="client-email-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('Phone')}</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    data-testid="client-phone-input"
                  />
                </div>

                <button
                  className="cta-button"
                  onClick={handleBooking}
                  disabled={loading}
                  style={{ width: '100%', marginTop: '1rem', justifyContent: 'center' }}
                  data-testid="confirm-booking-button"
                >
                  {loading ? 'Processing...' : t('Confirm Booking')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeachingJulieGrevenPage;